import React, { FC, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import RootStoreContext from '../../../model/RootStoreContext';
import { IDistribution } from '../../../model/distributions/Distribution';
import { ClaimType } from '@yakoffice/publisher-types';
import { useNavigate } from 'react-router';
import { useGetDistributionDiffLink, useGetDistributionPreviewLink } from '../../routes';
import Dropdown from 'react-bootstrap/cjs/Dropdown';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import { CompareDistributionsButton } from './CompareDistributionsButton';
import { usePublishSnackbarNotification } from '@yakoffice/snackbar';
import { ActionData, FloatingActionButton, FloatingActions } from '@yakoffice/floating-actions';
import { useShowModal } from '@yakoffice/custom-modal';


interface PropsType {
    hasDistributions: boolean,
    checkedDistributions: IDistribution[],
    handleDistribute: () => void,
    handleCopyDistribution: (toGameEnvironment: IGameEnvironment) => void,
    handleDownloadAsJson: () => void
    distributions: IDistribution[] | null
}


export const DistributionsNav: FC<PropsType> = observer((props) => {

    const rootStore = useContext(RootStoreContext);
    const navigate = useNavigate();
    const publishNotification = usePublishSnackbarNotification();
    const getDistributionDiffLink = useGetDistributionDiffLink();
    const getDistributionPreviewLink = useGetDistributionPreviewLink();
    const showModal = useShowModal();

    const handleComparePublishedWithLastDistribution = (diffGameVersion: IGameVersion, diffGameEnvironment: IGameEnvironment, rightDiff: boolean) => {
        rootStore.distributionStore.loadDistributions({
            gameVersionId: diffGameVersion.id,
            gameEnvironmentId: diffGameEnvironment.id,
            pageSize: 1,
            pageNumber: 1,
            hideFailed: true
        }, true)
            .then((distributions) => {

                if (distributions == null || distributions.length === 0) {
                    publishNotification({
                        text: 'No Distribution Found'
                    });
                    return;
                }

                navigate(getDistributionDiffLink({
                    gameVersionId: rightDiff ? rootStore.gameVersionStore.getCurrentGameVersion().id : diffGameVersion.id,
                    gameEnvironmentId: rightDiff ? rootStore.gameEnvironmentsStore.getCurrentGameEnvironment().id : diffGameEnvironment.id,
                    distributionId: rightDiff ? 'published' : distributions[0].id,
                    diffGameVersionId: rightDiff ? diffGameVersion.id : rootStore.gameVersionStore.getCurrentGameVersion().id,
                    diffGameEnvironmentId: rightDiff ? diffGameEnvironment.id : rootStore.gameEnvironmentsStore.getCurrentGameEnvironment().id,
                    diffDistributionId: rightDiff ? distributions[0].id : 'published'
                }));
            })
            .catch(e => publishNotification({
                text: `Error loading distributions`,
                canClose: true
            }));
    };

    const copyDistributionActions = () => {

        return rootStore.gameEnvironmentsStore.gameEnvironments.filter(ge => (props.checkedDistributions.length === 1 ? props.checkedDistributions[0] : null) || (ge !== rootStore.gameEnvironmentsStore.getCurrentGameEnvironment())).filter(ge => rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditEntity, ge)).map(ge =>
            <Dropdown.Item key={ge.id}
                           onClick={() => props.handleCopyDistribution(ge)}>{(props.checkedDistributions.length === 1 ? props.checkedDistributions[0] : null)?.id ?? 'Published'} to {ge.name}</Dropdown.Item>);
    };

    const diffComparison = () => {

        props.distributions && props.distributions.length > 0 &&
        showModal({
            show: true,
            title: 'Compare distributions',
            body: <CompareDistributionsButton
                distributions={props.distributions}
                checkedDistributions={props.checkedDistributions}
                handleComparePublishedWithLastDistribution={handleComparePublishedWithLastDistribution} />,
            canClose: false,
            hideFooter: true
        });
    };

    const distributionActions = (): ActionData[] => {

        const distributionData: ActionData[] = [{
            icon: 'fas fa-eye',
            tooltipText: 'Preview',
            subData: [
                <Dropdown.Item key="previewChanges"
                               onClick={() => handleComparePublishedWithLastDistribution(rootStore.gameVersionStore.getCurrentGameVersion(), rootStore.gameEnvironmentsStore.getCurrentGameEnvironment(), true)}>Changes</Dropdown.Item>,
                <Dropdown.Item key="previewAll" onClick={() => navigate(getDistributionPreviewLink())}>All
                    Published</Dropdown.Item>
            ]
        }];


        if (rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.DistributeEntities)) {
            distributionData.push({
                icon: 'fas fa-download',
                tooltipText: 'Download',
                callback: props.handleDownloadAsJson
            });
        }

        if (rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.DistributeEntities)) {
            distributionData.push({
                icon: 'fas fa-code-compare',
                tooltipText: 'Compare',
                callback: diffComparison
            });
        }

        if (rootStore.gameEnvironmentsStore.gameEnvironments
            .filter(ge => ge !== rootStore.gameEnvironmentsStore.getCurrentGameEnvironment())
            .find(ge => rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditEntity, ge)) && props.checkedDistributions.length < 2) {
            distributionData.push({
                icon: 'fas fa-copy',
                tooltipText: 'Copy',
                subData: copyDistributionActions()
            });
        }
        return distributionData;
    };

    return (
        <>
            <FloatingActions actions={distributionActions()} position="left" />
            {rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.DistributeEntities) &&
                <FloatingActionButton callback={props.handleDistribute}
                                      buttonContent={<><i className="fas fa-play-circle font-light me-2" /> Run
                                          Distribution</>} />
            }
        </>
    );
});
